import React, { useEffect, useMemo, useState } from 'react';
import {
  Megaphone, Plus, Pencil, Trash2, Power, RefreshCw, Save, X, Search, LogIn, Building2, Monitor, Clock, CheckCircle2, AlertCircle,
} from 'lucide-react';
import { ApiClient } from '../../api/client';
import type { AnnouncementLevel, SystemAnnouncementAdmin, SystemAnnouncementInput, Tenant } from '../../types';
import { AnnouncementCard, BannerStrip, TickerStrip, announcementLevelStyle } from '../announcements/Announcements';

// ═══════════════════════════════════════════════════════════════
// Rendszerüzenetek kezelése (superadmin)
// Hol jelenhet meg egy üzenet:
//   • belépés után: fényújság vagy rögzített sáv – minden vagy kiválasztott szervezet
//   • a központi belépési oldalon
//   • kiválasztott szervezetek egyedi belépési oldalán
// ═══════════════════════════════════════════════════════════════

const LEVELS: { id: AnnouncementLevel; label: string }[] = [
  { id: 'info', label: 'Tájékoztató' },
  { id: 'success', label: 'Hír / újdonság' },
  { id: 'warning', label: 'Figyelmeztetés' },
  { id: 'critical', label: 'Fontos / sürgős' },
];

const emptyForm = (): SystemAnnouncementInput => ({
  title: '', message: '', level: 'info', link_url: '', link_label: '',
  show_in_app: true, app_style: 'ticker', app_scope: 'all', app_tenant_ids: [],
  show_on_central_login: false, login_tenant_ids: [],
  dismissible: true, starts_at: null, ends_at: null, active: true,
});

// ISO (UTC) ↔ datetime-local (helyi idő)
const isoToLocal = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const localToIso = (v: string) => (v ? new Date(v).toISOString() : null);
const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');

function statusOf(a: SystemAnnouncementAdmin): { label: string; cls: string } {
  if (!a.active) return { label: 'Kikapcsolva', cls: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400' };
  const now = Date.now();
  if (a.starts_at && Date.parse(a.starts_at) > now) return { label: `Ütemezve: ${fmt(a.starts_at)}`, cls: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300' };
  if (a.ends_at && Date.parse(a.ends_at) <= now) return { label: 'Lejárt', cls: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400' };
  return { label: 'Most látható', cls: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300' };
}

// ── Szervezetválasztó (keresővel) ────────────────────────────
const TenantPicker: React.FC<{ tenants: Tenant[]; value: number[]; onChange: (v: number[]) => void; label: string }> = ({ tenants, value, onChange, label }) => {
  const [q, setQ] = useState('');
  const list = tenants.filter((t) => !q || `${t.name} ${t.slug}`.toLowerCase().includes(q.toLowerCase()));
  const toggle = (id: number) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950">
      <div className="flex items-center gap-2 px-2.5 py-1.5 border-b border-slate-100 dark:border-slate-800">
        <Search className="w-3.5 h-3.5 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`${label} – keresés`} className="flex-1 bg-transparent text-xs outline-none py-1" />
        <button type="button" onClick={() => onChange([...new Set([...value, ...list.map((t) => t.id)])])} className="text-[11px] text-indigo-600 hover:underline cursor-pointer">Mind</button>
        <button type="button" onClick={() => onChange([])} className="text-[11px] text-slate-500 hover:underline cursor-pointer">Egyik sem</button>
      </div>
      <div className="max-h-44 overflow-y-auto p-1.5 grid sm:grid-cols-2 gap-x-2">
        {list.map((t) => (
          <label key={t.id} className="flex items-center gap-2 px-1.5 py-1 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-900 text-xs cursor-pointer min-w-0">
            <input type="checkbox" checked={value.includes(t.id)} onChange={() => toggle(t.id)} />
            <span className="truncate">{t.name}</span>
            {t.is_demo ? <span className="text-[9px] px-1 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 shrink-0">DEMO</span> : null}
          </label>
        ))}
        {!list.length && <div className="text-xs text-slate-400 p-2">Nincs találat</div>}
      </div>
      <div className="px-2.5 py-1 border-t border-slate-100 dark:border-slate-800 text-[11px] text-slate-500">{value.length} kiválasztva</div>
    </div>
  );
};

export const SystemAnnouncementsView: React.FC = () => {
  const [items, setItems] = useState<SystemAnnouncementAdmin[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<SystemAnnouncementInput | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const tenantName = useMemo(() => new Map(tenants.map((t) => [t.id, t.name])), [tenants]);

  const load = async () => {
    setLoading(true);
    const [a, t] = await Promise.all([ApiClient.listAnnouncementsAdmin(), ApiClient.getTenants()]);
    if (a.ok && a.data) setItems(a.data); else setError(a.error || 'Nem sikerült betölteni az üzeneteket');
    if (t.ok && t.data) setTenants([...t.data].sort((x, y) => x.name.localeCompare(y.name, 'hu')));
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const notifyChanged = () => window.dispatchEvent(new Event('kozad:announcements-changed'));

  const startNew = () => { setEditingId(null); setForm(emptyForm()); setError(null); };
  const startEdit = (a: SystemAnnouncementAdmin) => {
    setEditingId(a.id); setError(null);
    setForm({
      title: a.title || '', message: a.message, level: a.level, link_url: a.link_url || '', link_label: a.link_label || '',
      show_in_app: a.show_in_app, app_style: a.app_style, app_scope: a.app_tenant_ids ? 'tenants' : 'all', app_tenant_ids: a.app_tenant_ids || [],
      show_on_central_login: a.show_on_central_login, login_tenant_ids: a.login_tenant_ids || [],
      dismissible: a.dismissible, starts_at: a.starts_at, ends_at: a.ends_at, active: a.active,
    });
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    setSaving(true); setError(null);
    const r = await ApiClient.saveAnnouncement(form, editingId ?? undefined);
    setSaving(false);
    if (!r.ok) { setError(r.error || 'Mentési hiba'); return; }
    setForm(null); setEditingId(null);
    setNotice(editingId ? 'Az üzenet módosítva.' : 'Az üzenet létrehozva.');
    setTimeout(() => setNotice(null), 3000);
    await load(); notifyChanged();
  };

  const toggleActive = async (a: SystemAnnouncementAdmin) => {
    const input: SystemAnnouncementInput = {
      title: a.title, message: a.message, level: a.level, link_url: a.link_url || '', link_label: a.link_label || '',
      show_in_app: a.show_in_app, app_style: a.app_style, app_scope: a.app_tenant_ids ? 'tenants' : 'all', app_tenant_ids: a.app_tenant_ids || [],
      show_on_central_login: a.show_on_central_login, login_tenant_ids: a.login_tenant_ids, dismissible: a.dismissible,
      starts_at: a.starts_at, ends_at: a.ends_at, active: !a.active,
    };
    const r = await ApiClient.saveAnnouncement(input, a.id);
    if (!r.ok) { setError(r.error || 'Hiba'); return; }
    await load(); notifyChanged();
  };

  const remove = async (a: SystemAnnouncementAdmin) => {
    if (!window.confirm(`Biztosan törlöd ezt az üzenetet?\n\n„${a.title || a.message.slice(0, 80)}”\n\nHa csak ideiglenesen nem kell, használd inkább a kikapcsolást.`)) return;
    const r = await ApiClient.deleteAnnouncement(a.id);
    if (!r.ok) { setError(r.error || 'Törlési hiba'); return; }
    await load(); notifyChanged();
  };

  const where = (a: SystemAnnouncementAdmin) => {
    const out: string[] = [];
    if (a.show_in_app) {
      const style = a.app_style === 'ticker' ? 'fényújság' : 'rendszerüzenet-sáv';
      out.push(a.app_tenant_ids ? `Belépés után (${style}): ${a.app_tenant_ids.length} szervezet` : `Belépés után (${style}): minden szervezet`);
    }
    if (a.show_on_central_login) out.push('Központi belépési oldal');
    if (a.login_tenant_ids?.length) {
      const names = a.login_tenant_ids.map((id) => tenantName.get(id) || `#${id}`);
      out.push(`Szervezeti belépési oldal: ${names.slice(0, 3).join(', ')}${names.length > 3 ? ` +${names.length - 3}` : ''}`);
    }
    return out;
  };

  const set = <K extends keyof SystemAnnouncementInput>(k: K, v: SystemAnnouncementInput[K]) => setForm((f) => (f ? { ...f, [k]: v } : f));
  const previewItem = form ? {
    id: 0, title: form.title || '', message: form.message || 'Az üzenet szövege itt jelenik meg…', level: form.level,
    link_url: form.link_url || null, link_label: form.link_label || null, app_style: form.app_style, dismissible: form.dismissible, updated_at: '',
  } : null;

  const inputCls = 'w-full px-3 py-2 bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500';
  const labelCls = 'block text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5';

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-5 min-w-0 w-full">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400"><Megaphone className="w-5 h-5" /></div>
          <div>
            <h1 className="text-lg font-bold text-slate-900 dark:text-white">Rendszerüzenetek</h1>
            <p className="text-xs text-slate-500 dark:text-slate-400 max-w-2xl">
              Tájékoztató a felhasználóknak: belépés után fényújságként vagy rögzített sávként, valamint a központi és a szervezeti belépési oldalakon. Időzíthető, és szervezetre szűkíthető.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 cursor-pointer" title="Frissítés"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></button>
          {!form && (
            <button onClick={startNew} className="px-3.5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold flex items-center gap-1.5 cursor-pointer"><Plus className="w-4 h-4" /> Új üzenet</button>
          )}
        </div>
      </div>

      {notice && <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-sm text-emerald-700 dark:text-emerald-300 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />{notice}</div>}
      {error && <div className="p-3 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-sm text-rose-700 dark:text-rose-300 flex items-center gap-2"><AlertCircle className="w-4 h-4" />{error}</div>}

      {/* ── Szerkesztő ── */}
      {form && previewItem && (
        <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,380px)] gap-5 p-4 sm:p-5 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 min-w-0 overflow-x-clip">
          <div className="space-y-5 min-w-0">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-slate-900 dark:text-white">{editingId ? 'Üzenet szerkesztése' : 'Új üzenet'}</h2>
              <button type="button" onClick={() => { setForm(null); setEditingId(null); }} className="p-1 text-slate-400 hover:text-slate-700 cursor-pointer"><X className="w-4 h-4" /></button>
            </div>

            {/* Tartalom */}
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Jelleg</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {LEVELS.map((l) => {
                    const Icon = announcementLevelStyle[l.id].icon;
                    const on = form.level === l.id;
                    return (
                      <button key={l.id} type="button" onClick={() => set('level', l.id)}
                        className={`flex items-center gap-1.5 px-2.5 py-2 rounded-xl border text-xs font-semibold cursor-pointer ${on ? announcementLevelStyle[l.id].card + ' ring-2 ring-offset-1 ring-indigo-400 dark:ring-offset-slate-900' : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'}`}>
                        <Icon className="w-3.5 h-3.5" /> {l.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className={labelCls}>Cím (nem kötelező)</label>
                <input value={form.title} maxLength={120} onChange={(e) => set('title', e.target.value)} placeholder="pl. Karbantartás" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Üzenet *</label>
                <textarea value={form.message} maxLength={1000} rows={3} required style={{ resize: 'vertical' }} onChange={(e) => set('message', e.target.value)} placeholder="pl. 2026. október 20-án 18 és 20 óra között a rendszer karbantartás miatt nem érhető el." className={inputCls} />
                <div className="text-[10px] text-slate-400 text-right">{form.message.length}/1000 · sima szöveg</div>
              </div>
              <div className="grid sm:grid-cols-[1fr_180px] gap-2">
                <div>
                  <label className={labelCls}>Hivatkozás (nem kötelező)</label>
                  <input value={form.link_url} onChange={(e) => set('link_url', e.target.value)} placeholder="https://…" className={inputCls + ' font-mono text-xs'} />
                </div>
                <div>
                  <label className={labelCls}>Hivatkozás szövege</label>
                  <input value={form.link_label} maxLength={60} onChange={(e) => set('link_label', e.target.value)} placeholder="Részletek" className={inputCls} />
                </div>
              </div>
            </div>

            {/* Belépés után */}
            <fieldset className="space-y-2.5 p-3.5 rounded-xl border border-slate-200 dark:border-slate-800">
              <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                <input type="checkbox" checked={form.show_in_app} onChange={(e) => set('show_in_app', e.target.checked)} />
                <Monitor className="w-4 h-4 text-indigo-500" /> Megjelenítés belépés után
              </label>
              {form.show_in_app && (
                <div className="space-y-2.5 pl-6">
                  <div className="flex flex-wrap gap-2">
                    {([['ticker', 'Fényújság (futó szöveg)'], ['banner', 'Rendszerüzenet-sáv (rögzített)']] as const).map(([id, l]) => (
                      <label key={id} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs cursor-pointer ${form.app_style === id ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-950/40' : 'border-slate-200 dark:border-slate-700'}`}>
                        <input type="radio" name="app_style" checked={form.app_style === id} onChange={() => set('app_style', id)} /> {l}
                      </label>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {([['all', 'Minden szervezetnél'], ['tenants', 'Csak a kiválasztott szervezeteknél']] as const).map(([id, l]) => (
                      <label key={id} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs cursor-pointer ${form.app_scope === id ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-950/40' : 'border-slate-200 dark:border-slate-700'}`}>
                        <input type="radio" name="app_scope" checked={form.app_scope === id} onChange={() => set('app_scope', id)} /> {l}
                      </label>
                    ))}
                  </div>
                  {form.app_scope === 'tenants' && (
                    <TenantPicker tenants={tenants} value={form.app_tenant_ids} onChange={(v) => set('app_tenant_ids', v)} label="Szervezetek" />
                  )}
                </div>
              )}
            </fieldset>

            {/* Belépési oldalak */}
            <fieldset className="space-y-2.5 p-3.5 rounded-xl border border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-2 text-sm font-semibold"><LogIn className="w-4 h-4 text-indigo-500" /> Belépési oldalak</div>
              <label className="flex items-center gap-2 text-xs pl-6 cursor-pointer">
                <input type="checkbox" checked={form.show_on_central_login} onChange={(e) => set('show_on_central_login', e.target.checked)} />
                Központi belépési oldal
              </label>
              <div className="pl-6 space-y-1.5">
                <div className="text-xs flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-slate-400" /> Szervezetek egyedi belépési oldala</div>
                <TenantPicker tenants={tenants} value={form.login_tenant_ids} onChange={(v) => set('login_tenant_ids', v)} label="Belépési oldalak" />
              </div>
            </fieldset>

            {/* Időzítés */}
            <fieldset className="space-y-2.5 p-3.5 rounded-xl border border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-2 text-sm font-semibold"><Clock className="w-4 h-4 text-indigo-500" /> Időzítés és viselkedés</div>
              <div className="grid sm:grid-cols-2 gap-2 pl-6">
                <div>
                  <label className={labelCls}>Megjelenés kezdete</label>
                  <input type="datetime-local" value={isoToLocal(form.starts_at ?? null)} onChange={(e) => set('starts_at', localToIso(e.target.value))} className={inputCls} />
                  <div className="text-[10px] text-slate-400 mt-0.5">Üresen: azonnal</div>
                </div>
                <div>
                  <label className={labelCls}>Megjelenés vége</label>
                  <input type="datetime-local" value={isoToLocal(form.ends_at ?? null)} onChange={(e) => set('ends_at', localToIso(e.target.value))} className={inputCls} />
                  <div className="text-[10px] text-slate-400 mt-0.5">Üresen: kikapcsolásig</div>
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs pl-6 cursor-pointer">
                <input type="checkbox" checked={form.dismissible} onChange={(e) => set('dismissible', e.target.checked)} />
                A felhasználó elrejtheti belépés után (módosítás után újra megjelenik)
              </label>
              <label className="flex items-center gap-2 text-xs pl-6 cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} />
                Bekapcsolva
              </label>
            </fieldset>

            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setForm(null); setEditingId(null); }} className="px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-sm cursor-pointer">Mégse</button>
              <button type="submit" disabled={saving} className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold flex items-center gap-1.5 cursor-pointer disabled:opacity-50">
                <Save className="w-4 h-4" /> {saving ? 'Mentés…' : 'Mentés'}
              </button>
            </div>
          </div>

          {/* Előnézet */}
          <div className="space-y-3 lg:sticky lg:top-4 self-start min-w-0 w-full overflow-hidden">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Előnézet</div>
            {form.show_in_app && (
              <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800">
                <div className="h-7 bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 flex items-center px-2 text-[10px] text-slate-400">felső sáv</div>
                {form.app_style === 'ticker' ? <TickerStrip items={[previewItem]} /> : <BannerStrip a={previewItem} />}
                <div className="h-14 bg-slate-50 dark:bg-slate-900" />
              </div>
            )}
            {(form.show_on_central_login || form.login_tenant_ids.length > 0) && (
              <div className="p-3 rounded-xl bg-slate-100 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 space-y-2">
                <div className="text-[10px] text-slate-400">belépési oldal</div>
                <AnnouncementCard a={previewItem} />
                <div className="h-10 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800" />
              </div>
            )}
            {!form.show_in_app && !form.show_on_central_login && form.login_tenant_ids.length === 0 && (
              <p className="text-xs text-amber-600">Válassz legalább egy megjelenési helyet.</p>
            )}
          </div>
        </form>
      )}

      {/* ── Lista ── */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800">
        {!loading && items.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-500">Még nincs rendszerüzenet. Az „Új üzenet” gombbal hozhatsz létre egyet.</div>
        )}
        {items.map((a) => {
          const st = statusOf(a);
          const Icon = announcementLevelStyle[a.level].icon;
          return (
            <div key={a.id} className="p-4 flex flex-col sm:flex-row sm:items-start gap-3">
              <div className={`p-2 rounded-lg shrink-0 self-start ${announcementLevelStyle[a.level].card}`}><Icon className="w-4 h-4" /></div>
              <div className="flex-1 min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-slate-900 dark:text-white">{a.title || <span className="text-slate-400 font-normal">(cím nélkül)</span>}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${st.cls}`}>{st.label}</span>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-2 whitespace-pre-line">{a.message}</p>
                <div className="flex flex-wrap gap-1.5">
                  {where(a).map((w) => <span key={w} className="text-[10px] px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">{w}</span>)}
                  {a.ends_at && <span className="text-[10px] px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-500">vége: {fmt(a.ends_at)}</span>}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => toggleActive(a)} className={`p-2 rounded-lg border cursor-pointer ${a.active ? 'border-emerald-300 text-emerald-600' : 'border-slate-200 dark:border-slate-700 text-slate-400'}`} title={a.active ? 'Kikapcsolás' : 'Bekapcsolás'}><Power className="w-4 h-4" /></button>
                <button onClick={() => startEdit(a)} className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-indigo-600 cursor-pointer" title="Szerkesztés"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(a)} className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-rose-600 cursor-pointer" title="Törlés"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SystemAnnouncementsView;
